/**
 * Hook for fetching document connections
 * 
 * Returns all documents linked to the source document, grouped by target doctype.
 * Fully generic - works for any doctype.
 */

import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { DocConnectionsResponse } from "@/lib/erp/docs";

/**
 * Hook to fetch document connections for a given doctype and name.
 * 
 * @param doctype - Source document type (e.g., "Sales Order", "Item")
 * @param name - Source document name (e.g., "SO-0001", "ITEM-001")
 * @param options - Optional configuration including enabled flag
 * @returns React Query result with connections data
 */
export function useDocConnections(
  doctype: string,
  name?: string,
  options?: { enabled?: boolean }
): UseQueryResult<DocConnectionsResponse | null, unknown> {
  return useQuery<DocConnectionsResponse | null>({
    queryKey: ["doc-connections", doctype, name],
    queryFn: async () => {
      if (!name) {
        return null;
      }
      
      try {
        return await erp.docs.getDocConnections(doctype, name);
      } catch (error) {
        console.error("Failed to fetch doc connections:", error);
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!name && !!doctype && name !== "new",
    staleTime: 0, // No cache - always refetch (temporary for debugging)
    gcTime: 0, // Don't keep in cache (@tanstack/react-query v5)
  });
}
